import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    IconButton,
    Tooltip,
    Popover,
    Typography,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert
} from '@mui/material';
import {
    SwapHoriz as ConvertIcon,
    ContentCopy as CopyIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { ethers } from 'ethers';
import { copyToClipboard } from '../../utils/hexConverter';
import logger from '../../utils/logger';

interface WeiInputConverterProps {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    tokenSymbol?: string;
    tokenDecimals?: number;
}

/**
 * Wei Input Converter - Inline converter button for uint256 input fields
 * Helps users convert human-readable amounts to Wei format
 */
const WeiInputConverter: React.FC<WeiInputConverterProps> = ({
    value,
    onChange,
    label = 'Amount',
    tokenSymbol = 'MEMER',
    tokenDecimals = 18
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [humanInput, setHumanInput] = useState('');
    const [conversionType, setConversionType] = useState<'token' | 'usd' | 'eth'>('token');
    const [weiOutput, setWeiOutput] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);

    // Open converter popover
    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);

        // Try to parse current value as wei and show human-readable
        if (value && value !== '' && value !== '0') {
            try {
                const formatted = ethers.utils.formatUnits(value, tokenDecimals);
                setHumanInput(formatted);
                setWeiOutput(value);
            } catch (error) {
                setHumanInput('');
                setWeiOutput('');
            }
        } else {
            setHumanInput('');
            setWeiOutput('');
        }
    };

    // Close converter popover
    const handleClose = () => {
        setAnchorEl(null);
        setCopySuccess(false);
    };

    const open = Boolean(anchorEl);

    // Convert human-readable to wei
    useEffect(() => {
        if (!humanInput || humanInput === '') {
            setWeiOutput('');
            return;
        }

        try {
            let wei = '';

            if (conversionType === 'token' || conversionType === 'eth') {
                // Parse as ether (18 decimals)
                wei = ethers.utils.parseUnits(humanInput, tokenDecimals).toString();
            } else if (conversionType === 'usd') {
                // USD is also 18 decimals in our contracts
                wei = ethers.utils.parseUnits(humanInput, 18).toString();
            }

            setWeiOutput(wei);
            logger.log('Converted to wei:', { humanInput, wei, type: conversionType });
        } catch (error) {
            logger.warn('Conversion error:', error);
            setWeiOutput('');
        }
    }, [humanInput, conversionType, tokenDecimals]);

    // Handle paste to input field
    const handlePasteToInput = () => {
        if (weiOutput) {
            onChange(weiOutput);
            handleClose();
        }
    };

    // Handle copy to clipboard
    const handleCopy = async () => {
        if (weiOutput) {
            const success = await copyToClipboard(weiOutput);
            if (success) {
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            }
        }
    };

    return (
        <>
            <Tooltip title="Convert to Wei format">
                <IconButton
                    size="small"
                    onClick={handleOpen}
                    sx={{
                        bgcolor: 'action.hover',
                        '&:hover': { bgcolor: 'action.selected' }
                    }}
                >
                    <ConvertIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                sx={{ mt: 1 }}
            >
                <Box sx={{ p: 2, width: 350 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Convert to Wei
                        </Typography>
                        <IconButton size="small" onClick={handleClose}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                        Enter amount in human-readable format
                    </Alert>

                    {/* Conversion Type Selector */}
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                            value={conversionType}
                            label="Type"
                            onChange={(e) => setConversionType(e.target.value as any)}
                        >
                            <MenuItem value="token">
                                {tokenSymbol} Amount ({tokenDecimals} decimals)
                            </MenuItem>
                            <MenuItem value="usd">
                                USD Amount ($) (18 decimals)
                            </MenuItem>
                            <MenuItem value="eth">
                                ETH Amount (18 decimals)
                            </MenuItem>
                        </Select>
                    </FormControl>

                    {/* Human-Readable Input */}
                    <TextField
                        label={`${conversionType === 'token' ? tokenSymbol : conversionType.toUpperCase()} Amount`}
                        value={humanInput}
                        onChange={(e) => setHumanInput(e.target.value)}
                        fullWidth
                        size="small"
                        type="number"
                        placeholder={
                            conversionType === 'token'
                                ? '10000000'
                                : conversionType === 'usd'
                                    ? '50'
                                    : '1.5'
                        }
                        helperText={
                            conversionType === 'token'
                                ? `Example: 10,000,000 ${tokenSymbol}`
                                : conversionType === 'usd'
                                    ? 'Example: 50 (for $50 USD)'
                                    : 'Example: 1.5 (for 1.5 ETH)'
                        }
                        sx={{ mb: 2 }}
                        autoFocus
                    />

                    <Divider sx={{ my: 2 }} />

                    {/* Wei Output */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                            Wei Format (uint256):
                        </Typography>
                        <Box sx={{
                            p: 1.5,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            wordBreak: 'break-all',
                            minHeight: 40
                        }}>
                            {weiOutput || <em style={{ color: '#999' }}>Enter amount above</em>}
                        </Box>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handlePasteToInput}
                            disabled={!weiOutput}
                            size="small"
                        >
                            Paste to Field
                        </Button>
                        <Tooltip title={copySuccess ? 'Copied!' : 'Copy to clipboard'}>
                            <span>
                                <IconButton
                                    onClick={handleCopy}
                                    disabled={!weiOutput}
                                    size="small"
                                    sx={{
                                        bgcolor: copySuccess ? 'success.light' : 'action.hover',
                                        '&:hover': { bgcolor: copySuccess ? 'success.light' : 'action.selected' }
                                    }}
                                >
                                    <CopyIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>

                    {/* Quick Presets */}
                    {conversionType === 'token' && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                                Quick Presets:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                {[1000, 10000, 100000, 1000000, 10000000].map((amount) => (
                                    <Button
                                        key={amount}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setHumanInput(amount.toString())}
                                        sx={{ fontSize: '0.7rem' }}
                                    >
                                        {amount.toLocaleString()} {tokenSymbol}
                                    </Button>
                                ))}
                            </Box>
                        </>
                    )}

                    {conversionType === 'usd' && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                                Quick Presets:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                                {[1, 10, 50, 100, 200, 1000].map((amount) => (
                                    <Button
                                        key={amount}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setHumanInput(amount.toString())}
                                        sx={{ fontSize: '0.7rem' }}
                                    >
                                        ${amount}
                                    </Button>
                                ))}
                            </Box>
                        </>
                    )}
                </Box>
            </Popover>
        </>
    );
};

export default WeiInputConverter;
